import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Crown, CreditCard, Building2, Store, Check, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { getProfiles, getPlans, createPaymentPreference, createPaymentSubscription, processInlinePayment, processInlineSubscription } from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";
import { useAppConfig } from "../context/AppConfigContext";
import MpCardForm from "../components/MpCardForm";

const PAYMENT_METHODS = [
  { id: "credit_card", icon: CreditCard, recurring: true },
  { id: "spei", icon: Building2, recurring: false },
  { id: "cash", icon: Store, recurring: false },
];

export default function UpgradePage() {
  const { t, lang } = useTranslation();
  const { mpEnv, mpCheckoutMode, tiers } = useAppConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProfile = searchParams.get("profile");
  const preselectedTier = searchParams.get("tier");

  const [profiles, setProfiles] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedTier, setSelectedTier] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(preselectedProfile || "");
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [selectedMethod, setSelectedMethod] = useState("credit_card");
  const [recurring, setRecurring] = useState(true);
  const [showCardForm, setShowCardForm] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const isInline = mpCheckoutMode === "inline" && selectedMethod === "credit_card";

  // Paid tiers (position > 0 and has pricing)
  const paidTiers = tiers.filter(
    (t) => t.position > 0 && (t.price_monthly > 0 || t.price_quarterly > 0 || t.price_yearly > 0)
  );

  // Fetch profiles on mount
  useEffect(() => {
    getProfiles()
      .then((res) => {
        setAllProfiles(res.data.profiles);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Set default tier once tiers + profiles load
  useEffect(() => {
    if (paidTiers.length === 0 || selectedTier) return;
    if (preselectedTier) {
      setSelectedTier(preselectedTier);
      return;
    }
    // If a profile is preselected, default to the next tier above its current tier
    if (preselectedProfile && allProfiles.length > 0) {
      const profile = allProfiles.find((p) => p.slug === preselectedProfile);
      if (profile) {
        const profileTier = tiers.find((t) => t.name === profile.tier);
        const profilePos = profileTier ? profileTier.position : 0;
        const nextTier = paidTiers.find((t) => t.position > profilePos);
        if (nextTier) {
          setSelectedTier(nextTier.name);
          return;
        }
      }
    }
    setSelectedTier(paidTiers[0].name);
    // eslint-disable-next-line
  }, [tiers, allProfiles]);

  // Fetch plans when selected tier changes
  useEffect(() => {
    if (!selectedTier) return;
    getPlans(selectedTier)
      .then((res) => setPlans(res.data.plans))
      .catch(() => setPlans({}));
  }, [selectedTier]);

  // Filter profiles: show only those with a tier lower than the selected one
  useEffect(() => {
    if (!selectedTier || !allProfiles.length) return;
    const targetTier = tiers.find((t) => t.name === selectedTier);
    if (!targetTier) return;

    const eligible = allProfiles.filter((p) => {
      const profileTier = tiers.find((t) => t.name === p.tier);
      const profilePos = profileTier ? profileTier.position : 0;
      return profilePos < targetTier.position;
    });
    setProfiles(eligible);

    // Reset profile selection if current is not eligible
    if (eligible.length > 0) {
      if (!eligible.find((p) => p.slug === selectedProfile)) {
        setSelectedProfile(preselectedProfile && eligible.find((p) => p.slug === preselectedProfile)
          ? preselectedProfile
          : eligible[0].slug);
      }
    } else {
      setSelectedProfile("");
    }
    // eslint-disable-next-line
  }, [selectedTier, allProfiles, tiers]);

  useEffect(() => {
    const method = PAYMENT_METHODS.find((m) => m.id === selectedMethod);
    if (method && !method.recurring) {
      setRecurring(false);
    }
    setShowCardForm(false);
  }, [selectedMethod]);

  // Reset card form when plan, profile, or tier changes
  useEffect(() => {
    setShowCardForm(false);
  }, [selectedPlan, selectedProfile, selectedTier]);

  // Redirect mode checkout
  const handleRedirectCheckout = async () => {
    if (!selectedProfile || !selectedPlan || !selectedTier) return;
    setSubmitting(true);

    try {
      const payload = {
        profile_slug: selectedProfile,
        plan: selectedPlan,
        payment_method: selectedMethod,
        tier: selectedTier,
      };

      let res;
      if (recurring && selectedMethod === "credit_card") {
        res = await createPaymentSubscription(payload);
      } else {
        res = await createPaymentPreference(payload);
      }

      const url = mpEnv === "test"
        ? (res.data.sandbox_init_point || res.data.init_point)
        : res.data.init_point;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Error creating payment";
      const details = err.response?.data?.details || "";
      setPaymentError(details ? `${msg}: ${details}` : msg);
      console.error("Payment error:", err.response?.data);
    } finally {
      setSubmitting(false);
    }
  };

  // Inline mode: card form submitted with token
  const handleInlinePayment = async (cardFormData) => {
    if (!selectedProfile || !selectedPlan || !selectedTier) return;
    setSubmitting(true);

    try {
      const payload = {
        profile_slug: selectedProfile,
        plan: selectedPlan,
        tier: selectedTier,
        token: cardFormData.token,
        payment_method_id: cardFormData.payment_method_id,
        issuer_id: cardFormData.issuer_id,
        installments: cardFormData.installments,
      };

      let res;
      if (recurring) {
        res = await processInlineSubscription(payload);
      } else {
        res = await processInlinePayment(payload);
      }

      const st = res.data.status;
      const pid = res.data.payment_id;
      const detail = res.data.mp_status_detail || "";
      if (st === "approved") {
        navigate("/app/subscriptions?payment=success&pid=" + pid);
      } else if (st === "in_process" || st === "pending") {
        navigate("/app/subscriptions?payment=pending&pid=" + pid);
      } else {
        if (detail.includes("card_type_not_allowed") || detail.includes("not_supported")) {
          setPaymentError(t("upgrade.rejectedDebit"));
        } else {
          setPaymentError(t("upgrade.rejectedGeneric"));
        }
      }
    } catch (err) {
      console.error("Inline payment error:", err.response?.data);
      setPaymentError(t("upgrade.rejectedGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckout = () => {
    setPaymentError(null);
    if (isInline) {
      setShowCardForm(true);
    } else {
      handleRedirectCheckout();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const currentPlan = plans[selectedPlan];

  const lockedProfile = preselectedProfile
    ? profiles.find((p) => p.slug === preselectedProfile)
    : null;

  const tierLabel = (tier) => lang === "es" && tier.label_es ? tier.label_es : tier.label;

  return (
    <div className="max-w-2xl mx-auto pb-16">
      {/* Fixed back button below header */}
      <div className="fixed top-14 left-0 right-0 z-40 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-2.5 md:ml-56">
          <button
            onClick={() => navigate("/app/subscriptions")}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("upgrade.back")}
          </button>
        </div>
      </div>
      <div className="h-10" /> {/* Spacer for fixed back bar */}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Crown className="h-6 w-6 text-amber-500" />
          {t("upgrade.title")}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t("upgrade.subtitle")}</p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Select tier */}
        {paidTiers.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              {t("upgrade.selectTier")}
            </h2>
            <div className="grid gap-3">
              {paidTiers.map((tier) => (
                <button
                  key={tier.name}
                  onClick={() => setSelectedTier(tier.name)}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition text-left ${
                    selectedTier === tier.name
                      ? "border-amber-500 bg-amber-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: tier.color + "20", color: tier.color }}
                  >
                    <Crown className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{tierLabel(tier)}</p>
                    {(tier.features?.length > 0 || tier.features_es?.length > 0) && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {(lang === "es" && tier.features_es?.length > 0 ? tier.features_es : tier.features || [])
                          .slice(0, 3)
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">
                      ${tier.price_monthly.toFixed(2)}<span className="text-xs font-normal text-gray-500">/{t("upgrade.mo")}</span>
                    </p>
                  </div>
                  {selectedTier === tier.name && (
                    <Check className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No eligible profiles message */}
        {profiles.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">{t("upgrade.allUpgraded")}</p>
            <p className="text-sm text-gray-400 mt-1">{t("upgrade.allUpgradedDesc")}</p>
          </div>
        ) : (
          <>
            {/* Step 2: Profile */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              {!lockedProfile && (
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  {t("upgrade.selectProfile")}
                </h2>
              )}
              {lockedProfile ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {lockedProfile.profile_image ? (
                    <img src={lockedProfile.profile_image} alt="" className="h-9 w-9 rounded-full object-cover" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                      {lockedProfile.profile_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  )}
                  <span className="font-medium text-gray-800 text-sm">{lockedProfile.profile_name}</span>
                </div>
              ) : (
                <select
                  value={selectedProfile}
                  onChange={(e) => setSelectedProfile(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  {profiles.map((p) => {
                    const pTier = tiers.find((t) => t.name === p.tier);
                    const tierName = pTier ? tierLabel(pTier) : p.tier;
                    return (
                      <option key={p.slug} value={p.slug}>
                        {p.profile_name} ({tierName})
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* Step 3: Select plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                {t("upgrade.selectPlan")}
              </h2>
              <div className="grid gap-3">
                {Object.entries(plans).map(([key, plan]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPlan(key)}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition text-left ${
                      selectedPlan === key
                        ? "border-amber-500 bg-amber-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div>
                      <p className="font-semibold text-gray-900">{plan.label}</p>
                      <p className="text-xs text-gray-500">
                        {plan.months} {plan.months === 1 ? t("upgrade.month") : t("upgrade.months")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        ${plan.amount.toFixed(2)} <span className="text-xs font-normal text-gray-500">MXN</span>
                      </p>
                      {plan.months > 1 && (
                        <p className="text-xs text-green-600">
                          ${(plan.amount / plan.months).toFixed(2)}/{t("upgrade.mo")}
                        </p>
                      )}
                    </div>
                    {selectedPlan === key && (
                      <Check className="h-5 w-5 text-amber-500 ml-2 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 4: Payment method — only show for redirect mode */}
            {mpCheckoutMode === "redirect" && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  {t("upgrade.paymentMethod")}
                </h2>
                <div className="grid gap-3">
                  {PAYMENT_METHODS.map(({ id, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setSelectedMethod(id)}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 transition text-left ${
                        selectedMethod === id
                          ? "border-amber-500 bg-amber-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="h-5 w-5 text-gray-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{t(`upgrade.method.${id}`)}</p>
                        <p className="text-xs text-gray-500">{t(`upgrade.method.${id}_desc`)}</p>
                      </div>
                      {selectedMethod === id && (
                        <Check className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Recurring toggle for credit card */}
                {selectedMethod === "credit_card" && (
                  <label className="flex items-center gap-2 mt-4 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={recurring}
                      onChange={(e) => setRecurring(e.target.checked)}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{t("upgrade.autoRenew")}</p>
                      <p className="text-xs text-gray-500">{t("upgrade.autoRenewDesc")}</p>
                    </div>
                  </label>
                )}
              </div>
            )}

            {/* Inline mode: recurring toggle */}
            {mpCheckoutMode === "inline" && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recurring}
                    onChange={(e) => setRecurring(e.target.checked)}
                    className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t("upgrade.autoRenew")}</p>
                    <p className="text-xs text-gray-500">{t("upgrade.autoRenewDesc")}</p>
                  </div>
                </label>
              </div>
            )}

            {/* Payment error */}
            {paymentError && !(isInline && showCardForm) && (
              <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">{t("upgrade.paymentError")}</p>
                  <p className="mt-1 text-red-600">{paymentError}</p>
                </div>
                <button onClick={() => setPaymentError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none">&times;</button>
              </div>
            )}

            {/* Inline card form */}
            {isInline && showCardForm && currentPlan && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  {t("upgrade.cardDetails")}
                </h2>
                {paymentError && (
                  <div className="flex items-start gap-2 p-4 mb-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium">{t("upgrade.paymentError")}</p>
                      <p className="mt-1 text-red-600">{paymentError}</p>
                    </div>
                    <button onClick={() => setPaymentError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none">&times;</button>
                  </div>
                )}
                {submitting && (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-amber-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("upgrade.processing")}
                  </div>
                )}
                <MpCardForm
                  amount={currentPlan.amount}
                  onTokenReady={handleInlinePayment}
                  onError={(err) => console.error("Card form error:", err)}
                />
              </div>
            )}

            {/* Summary + checkout button */}
            {!(isInline && showCardForm) && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600">{t("upgrade.total")}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${currentPlan?.amount?.toFixed(2) || "0.00"}{" "}
                      <span className="text-sm font-normal text-gray-500">MXN</span>
                    </p>
                  </div>
                  <Crown className="h-8 w-8 text-amber-500" />
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={submitting || !selectedProfile}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("upgrade.processing")}
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      {t("upgrade.pay")}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {t("upgrade.secure")}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
